import { useDocumentHead } from "@/hooks/useDocumentHead.ts";

export function TermsPage() {
  useDocumentHead({
    title: "Terms of Service",
    description: "Read The Secret Trends terms of service. Understand the terms and conditions for using our website.",
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
      <p className="mt-2 text-sm text-gray-500">Last updated: March 20, 2026</p>

      <div className="mt-8 space-y-6 text-gray-600 leading-relaxed text-sm">
        <h2 className="text-lg font-semibold text-gray-900">1. Overview</h2>
        <p>
          This website is operated by The Secret Trends. By visiting our site and/or
          purchasing something from us, you engage in our "Service" and agree to be
          bound by the following terms and conditions, including any additional terms,
          conditions, and policies referenced herein. Please read these Terms of
          Service carefully before accessing or using our website.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          2. Online Terms
        </h2>
        <p>
          By agreeing to these Terms of Service, you represent that you are at least
          the age of majority in your state or province of residence. You may not use
          our products or services for any illegal or unauthorized purpose.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          3. General Conditions
        </h2>
        <p>
          We reserve the right to refuse service to anyone for any reason at any
          time. Your content (not including credit card information) may be
          transferred unencrypted and involve transmissions over various networks.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          4. Accuracy of Information
        </h2>
        <p>
          We are not responsible if information made available on this site is not
          accurate, complete, or current. The material on this site is provided for
          general information only.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          5. Products, Services, and Affiliate Disclosure
        </h2>
        <p>
          This Site operates as an advertising and review marketplace. We display
          products and link to third-party vendors. We may earn affiliate
          commissions on purchases made through our links. Prices for products are
          set by the third-party vendors and are subject to change without notice.
        </p>
        <p>
          <strong>FTC Disclosure:</strong> In accordance with the Federal Trade
          Commission's guidelines, we disclose that some or all of the links on this
          Site are affiliate links. This means we receive a commission if you click a
          link and make a purchase. This does not affect the price you pay. Our
          editorial content is not influenced by affiliate partnerships, and we only
          recommend products we believe in.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          6. Health Products Disclaimer
        </h2>
        <p>
          Content on this Site relating to health products is for informational
          purposes only and is not intended as medical advice. Statements about
          products have not been evaluated by the Food and Drug Administration.
          Products are not intended to diagnose, treat, cure, or prevent any disease.
          Always consult with a qualified healthcare professional before starting any
          new health product or supplement.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          7. Tracking Technologies and Data Collection
        </h2>
        <p>
          By using this Site, you acknowledge that we use cookies, tracking pixels,
          and similar technologies from third-party services including Google
          Analytics, Google Ads, Meta (Facebook/Instagram) Pixel, Pinterest Tag,
          TikTok Pixel, and affiliate tracking systems. These technologies collect
          data about your browsing behavior on our Site for purposes of analytics,
          advertising, conversion tracking, and audience building.
        </p>
        <p>
          By continuing to use the Site, you consent to the use of these
          technologies as described in our{" "}
          <a href="/privacy-policy" className="text-indigo-600 hover:underline">
            Privacy Policy
          </a>. You may opt out of targeted advertising at any time using the methods
          described in our Privacy Policy.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          8. Email Communications
        </h2>
        <p>
          When you subscribe to our newsletter or provide your email address, you
          consent to receiving promotional emails from us. Each email will include an
          unsubscribe link. We use third-party email services (Brevo) to manage our
          mailing lists. By subscribing, you acknowledge that your email address will
          be stored and processed by this service provider.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          9. Third-Party Links
        </h2>
        <p>
          Certain content, products, and services available via our Service may
          include materials from third parties. Third-party links on this site may
          direct you to websites that are not affiliated with us. We are not
          responsible for examining or evaluating the content, accuracy, privacy
          practices, or policies of third-party websites. We do not assume any
          responsibility or liability for any third-party materials, websites, or
          services.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          10. Third-Party Tools and Services
        </h2>
        <p>
          We may provide you with access to third-party tools and services which we
          neither monitor nor have any control over, including analytics platforms,
          advertising networks, social media platforms, and payment processors. You
          acknowledge that we provide access to such tools "as is" and "as available"
          without any warranties or conditions. We have no liability whatsoever
          arising from your use of third-party tools or services.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          11. User Comments and Feedback
        </h2>
        <p>
          If you send certain specific submissions or creative ideas, you agree that
          we may use, reproduce, modify, and distribute such content without
          restriction or compensation.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          12. Personal Information
        </h2>
        <p>
          Your submission of personal information through the site is governed by our{" "}
          <a href="/privacy-policy" className="text-indigo-600 hover:underline">
            Privacy Policy
          </a>.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          13. Prohibited Uses
        </h2>
        <p>
          You are prohibited from using the site for any unlawful purpose, to
          solicit others to perform unlawful acts, to violate any regulations, to
          infringe upon intellectual property rights, to harass or discriminate, to
          submit false information, to upload malicious code, or to interfere with
          the security features of the Site.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          14. Intellectual Property
        </h2>
        <p>
          All content on this Site, including text, graphics, logos, images, and
          software, is the property of The Secret Trends or its content suppliers and
          is protected by applicable intellectual property laws. You may not
          reproduce, distribute, modify, or create derivative works from any content
          on this Site without our express written permission.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          15. Disclaimer of Warranties
        </h2>
        <p>
          We do not guarantee that your use of our service will be uninterrupted,
          timely, secure, or error-free. The service and all products and content are
          provided on an "as is" and "as available" basis. We make no warranties,
          expressed or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          16. Limitation of Liability
        </h2>
        <p>
          In no event shall The Secret Trends, its directors, employees, partners,
          agents, or affiliates be liable for any indirect, incidental, special,
          consequential, or punitive damages arising out of your use of the Service.
          Our total liability shall not exceed the amount you paid to us, if any, in
          the twelve months prior to the claim.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          17. Indemnification
        </h2>
        <p>
          You agree to indemnify, defend, and hold harmless The Secret Trends and
          our affiliates from any claim or demand made by any third party due to or
          arising out of your breach of these Terms, your use of the Service, or
          your violation of any law or rights of a third party.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          18. Severability
        </h2>
        <p>
          If any provision of these Terms is determined to be unlawful or
          unenforceable, such provision shall be severed without affecting the
          validity of the remaining provisions.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          19. Termination
        </h2>
        <p>
          These Terms are effective unless and until terminated by either you or us.
          You may terminate these Terms at any time by discontinuing use of our Site.
          We may terminate or suspend your access immediately, without prior notice,
          for any reason.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          20. Governing Law
        </h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws
          of the United States, without regard to conflict of law principles.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          21. Changes to Terms
        </h2>
        <p>
          We reserve the right to update, change, or replace any part of these Terms
          by posting updates on our website. It is your responsibility to check this
          page periodically for changes. Your continued use of the Site after
          changes are posted constitutes acceptance of those changes.
        </p>

        <h2 className="text-lg font-semibold text-gray-900">
          22. Contact
        </h2>
        <p>
          Questions about the Terms of Service should be sent to{" "}
          <a href="mailto:info@thesecrettrends.com" className="text-indigo-600 hover:underline">
            info@thesecrettrends.com
          </a>.
        </p>
      </div>
    </div>
  );
}
